import { useCallback, useMemo } from 'react';
import { ArtistPortalEarningsGroup, ArtistPortalEarningsRow } from 'fym-shared/src/smartlinks/artistPortals';
import { useGetStreamingEarningsQuery } from '../../../../redux/api/fymAPI';

/** The rows of one currency. Two currencies are never added. */
export interface EarningsCurrencyGroup {
  currency: string | null;
  rows: ArtistPortalEarningsRow[];
}

/**
 * The earnings of every connected source that reports earnings, for whole months (design e1), one
 * group for each currency: the newest currency first, then `otherCurrencies`. A member who may not
 * see money gets `hidden`. `sources` names the sources of the amounts.
 */
export function useStreamingEarnings(input: {
  accountId: number;
  groupBy: ArtistPortalEarningsGroup;
  months: { from: string; to: string } | null;
  skip: boolean;
}) {
  const { accountId, groupBy, months, skip } = input;
  const { data, isLoading, error, refetch } = useGetStreamingEarningsQuery(
    { accountId, groupBy, from: months?.from, to: months?.to },
    { skip: skip || months === null }
  );
  const groups = useMemo((): EarningsCurrencyGroup[] => {
    if (data === undefined || data.rows === null) {
      return [];
    }
    const main: EarningsCurrencyGroup[] = data.rows.length === 0 ? [] : [{ currency: data.currency, rows: data.rows }];
    return [...main, ...(data.otherCurrencies ?? [])];
  }, [data]);
  const sources = useMemo(() => data?.sources ?? [], [data]);
  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);
  return { groups, sources, hidden: data?.earningsHidden ?? false, isLoading, error, retry };
}
